import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import LogisticsCompany from "@/enum/LogisticsCompany";
import OrderTag from "@/enum/OrderTag";
import { Modal } from "antd";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import { buildServerOptionsMap, deepGet, getPathKey } from "@/scenes/setting/WaybillTemplate/ListPage/util";
import ServiceOptionDTO from "@/dto/ewaybillAccount/ServiceOptionDTO";
import { isGenerateWaybillNoOnlyOrder } from "@/utils/OrderUtils";
import { confirm } from "@/utils";
import ShopType from "@/enum/ShopType";
import {
    checkAppointmentArrivalOrderTemplateRule,
    checkEncryptedManualOrderTemplateRule,
    checkJdConsolidationOrderTemplateRule,
    checkOutOrderTemplateRule,
    checkShipBySfOrderTemplateRule,
} from "@/utils/WaybillTemplateRule";
import { AppointmentArrivalUrl } from "@/utils/Url";
import GetWaybillNoMethod from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/GetWaybillNoMethod";
import GlobalStore from "@/store";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import { matchVirtualIdentifyNo } from "./RegExp";
import ShopDTO from "@/dto/shop/ShopDTO";
import WaybillTemplateField from "@/scenes/setting/WaybillTemplate/enum/WaybillTemplateField";
import WxEwaybillTipModal, {
    CHANNEL_SHOP_EWAYBILL_TIP_CACHE_2025_11_24,
} from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/components/WxEwaybillTipModal";
import EwaybillType from "@/enum/EwaybillType";
import { isAnyOrderWithAliWarehouse, isAnyOrderWithJDPetItem, isAnyOrderWithPddWarehouse } from "./OrderTagUtils";

export function checkDesignatedLogisticsCompany(orders: OrderDTO[], logisticsCompany: LogisticsCompany) {
    if (!orders) return false;

    if (!logisticsCompany) {
        return false;
    }

    for (const order of orders) {
        if (!!order.designatedLogisticsCompany && order?.designatedLogisticsCompany !== logisticsCompany) {
            return true;
        }
    }

    return false;
}

// 任意勾选打印订单包含orderTag=TMALL_LOGISTICS_UPGRADE且任意订单里的tmallLogisticsUpgrade.deliveryCps不包含所选模板对应的快递公司编码时需做以下确认再打印：
// 1、任意勾选打印订单包含 orderTag=TMALL_LOGISTICS_SHIP_BY_SF 但是所选模板非顺丰模板时提示“i、所选订单中包含承诺顺丰配送订单，当前所选模板对应快递公司不是顺丰快递可能导致时效无法达成被判责赔付，是否确认继续打印？”
// 2、任意勾选打印订单包含 orderTag=TMALL_LOGISTICS_SHIP_BY_SF 但是所选顺丰模板的快递产品类型不是“电商标快”时提示“i、所选订单中包含承诺顺丰配送订单，当前所选模板快递产品类型不是“电商标快”可能无法享受物流升级相关优惠政策，是否确认继续打印？”
// 3、任意勾选打印订单包含 orderTag=TMALL_LOGISTICS_SHIP_AS_REQUIRED 提示“i、所选订单中包含按需配送订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成被判责赔付，是否确认继续打印？”
// 4、任意勾选打印订单包含 orderTag=TMALL_LOGISTICS_PROMISE_SHIPPING 提示“i、所选订单中包含承诺发货订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成被判责赔付，是否确认继续打印？”
// 5、任意勾选打印订单包含 orderTag=TMALL_LOGISTICS_PROMISE_SERVED 提示“i、所选订单中包含承诺送达订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成被判责赔付，是否确认继续打印？”
// 6、所有勾选订单去重后如果只包含 orderTag=TMALL_LOGISTICS_UPGRADE 时统一提示“i、所选订单中包含物流升级订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成，是否确认继续打印？”
// 注意：上面提示语中的i是动态数字，匹配到上述多少个条件i的值就是多少

export async function checkTmallLogisticsUpgradeOrders(
    orders: OrderDTO[],
    waybillTemplate: WaybillTemplateDTO,
    ewaybillAccountMapping: Map<number, EwaybillAccountDTO>,
    ewaybillAccounts: EwaybillAccountDTO[],
) {
    let has_TMALL_LOGISTICS_SHIP_BY_SF = false;
    let has_TMALL_LOGISTICS_SHIP_AS_REQUIRED = false;
    let has_TMALL_LOGISTICS_PROMISE_SHIPPING = false;
    let has_TMALL_LOGISTICS_PROMISE_SERVED = false;
    let has_TMALL_LOGISTICS_UPGRADE = false;

    let allOrderIsTMALL_LOGISTICS_UPGRADE = false;
    let isSFBiaoKuai = false;
    const recommendedLogisticsCompanySet = new Set();

    for (let i = 0; i < orders.length; i++) {
        const order = orders[i];

        if (order?.tmallLogisticsUpgradeInfo?.designatedLogisticsCompanies) {
            order.tmallLogisticsUpgradeInfo.designatedLogisticsCompanies.forEach((it) => {
                recommendedLogisticsCompanySet.add(it);
            });
        }

        for (let j = 0; j < order.tags?.length; j++) {
            const tag = order.tags[j];

            if (tag === OrderTag.TMALL_LOGISTICS_SHIP_BY_SF) has_TMALL_LOGISTICS_SHIP_BY_SF = true;
            if (tag === OrderTag.TMALL_LOGISTICS_SHIP_AS_REQUIRED) has_TMALL_LOGISTICS_SHIP_AS_REQUIRED = true;
            if (tag === OrderTag.TMALL_LOGISTICS_PROMISE_SHIPPING) has_TMALL_LOGISTICS_PROMISE_SHIPPING = true;
            if (tag === OrderTag.TMALL_LOGISTICS_PROMISE_SERVED) has_TMALL_LOGISTICS_PROMISE_SERVED = true;
            if (tag === OrderTag.TMALL_LOGISTICS_UPGRADE) has_TMALL_LOGISTICS_UPGRADE = true;

            if (
                !has_TMALL_LOGISTICS_SHIP_BY_SF &&
                !has_TMALL_LOGISTICS_SHIP_AS_REQUIRED &&
                !has_TMALL_LOGISTICS_PROMISE_SHIPPING &&
                !has_TMALL_LOGISTICS_PROMISE_SERVED &&
                has_TMALL_LOGISTICS_UPGRADE
            ) {
                allOrderIsTMALL_LOGISTICS_UPGRADE = true;
            } else {
                allOrderIsTMALL_LOGISTICS_UPGRADE = false;
            }
        }
    }

    if (!recommendedLogisticsCompanySet.size) return;

    const isRecommendLogisticsCompanyTemplate = recommendedLogisticsCompanySet.has(waybillTemplate.logisticsCompany);

    if (isRecommendLogisticsCompanyTemplate) return;

    let index = 1;

    let contents = [];

    if (has_TMALL_LOGISTICS_SHIP_BY_SF && waybillTemplate.logisticsCompany !== LogisticsCompany.SF) {
        contents.push(
            `${index++}、所选订单中包含承诺顺丰配送订单，当前所选模板对应快递公司不是顺丰快递可能导致时效无法达成被判责赔付，是否确认继续打印?`,
        );
    }

    if (has_TMALL_LOGISTICS_SHIP_BY_SF && waybillTemplate.logisticsCompany === LogisticsCompany.SF) {
        const serverOptionsMap = buildServerOptionsMap(ewaybillAccounts);

        const { serviceOptions: serviceOptionsStr, logisticsCompany, ewaybillShippingAddressId } = waybillTemplate;

        const ewaybillType = ewaybillAccountMapping.get(ewaybillShippingAddressId)?.type;

        let serviceOption: ServiceOptionDTO;

        try {
            serviceOption = JSON.parse(serviceOptionsStr);
        } catch {}

        if (serviceOption) {
            Object.keys(serviceOption).forEach((serviceOptionKey, i) => {
                const option = serviceOption[serviceOptionKey];

                if (!option) return;

                Object.keys(option)?.forEach((attributeKey, j) => {
                    const serverNameKey = getPathKey(ewaybillType, logisticsCompany, serviceOptionKey, attributeKey);

                    const serverValueNameKey = getPathKey(serverNameKey, option[attributeKey]);

                    const serverValueName = deepGet(serverOptionsMap, serverValueNameKey);

                    if (serverValueName === "电商标快") {
                        isSFBiaoKuai = true;
                    }
                });
            });
        }

        if (!isSFBiaoKuai) {
            contents.push(
                `${index++}、所选订单中包含承诺顺丰配送订单，当前所选模板快递产品类型不是“电商标快”可能无法享受物流升级相关优惠政策，是否确认继续打印？`,
            );
        }
    }

    if (has_TMALL_LOGISTICS_SHIP_AS_REQUIRED) {
        contents.push(
            `${index++}、所选订单中包含按需配送订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成被判责赔付，是否确认继续打印？`,
        );
    }

    if (has_TMALL_LOGISTICS_PROMISE_SHIPPING) {
        contents.push(
            `${index++}、所选订单中包含承诺发货订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成被判责赔付，是否确认继续打印？`,
        );
    }

    if (has_TMALL_LOGISTICS_PROMISE_SERVED) {
        contents.push(
            `${index++}、所选订单中包含承诺送达订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成被判责赔付，是否确认继续打印？`,
        );
    }

    if (allOrderIsTMALL_LOGISTICS_UPGRADE) {
        contents.push(
            `${index++}、所选订单中包含物流升级订单，当前所选模板对应快递公司与推荐快递公司不一致可能导致时效无法达成，是否确认继续打印？`,
        );
    }

    if (contents.length) {
        await new Promise((yes, no) => {
            Modal.confirm({
                title: "是否继续打印",
                okText: "继续打印",
                width: 800,
                styles: { body: { padding: 0 } },
                content: (
                    <>
                        {contents.map((it, index) => {
                            return (
                                <div key={index} className="error">
                                    {it}
                                </div>
                            );
                        })}
                    </>
                ),
                okCancel: true,
                onOk() {
                    yes(null);
                },
                onCancel() {
                    no(new Error("用户取消操作"));
                },
            });
        });
    }
}

export async function checkGenerateWaybillNoOnlyOrders(orders: OrderDTO[], getWaybillNoMethod: GetWaybillNoMethod) {
    if (GetWaybillNoMethod.NEW !== getWaybillNoMethod) return;

    if (orders.some((order) => isGenerateWaybillNoOnlyOrder(order))) {
        await confirm("选中的订单里有订单已获取运单号，重复获取单号可能会多发包裹。确认继续获取新单号吗？");
    }
}

/** 检查特殊标签订单的模板是否符合规则 */
export function checkSpecialTagTemplateRule(
    globalStore: typeof GlobalStore,
    orders: OrderDTO[],
    template: WaybillTemplateDTO,
    shopType: ShopType,
) {
    const outOrderErrorMessage = checkOutOrderTemplateRule(orders, template, shopType);

    if (outOrderErrorMessage) {
        Modal.error({ title: "打印失败", content: outOrderErrorMessage });

        return;
    }

    const shipBySfErrorMessage = checkShipBySfOrderTemplateRule(orders, template);

    if (shipBySfErrorMessage) {
        Modal.error({ title: "打印失败", content: shipBySfErrorMessage });

        return;
    }

    const appointmentArrivalErrorMessage = checkAppointmentArrivalOrderTemplateRule(orders, template);

    if (appointmentArrivalErrorMessage) {
        Modal.error({
            title: "打印失败",
            content: (
                <div>
                    <p className="error">抖店平台要求承诺日达订单必须使用京东快递或者顺丰快递进行打印发货</p>
                    您可通过包含标签搜索所有承诺日达抖店进行批量打印，
                    <a href={AppointmentArrivalUrl} target="_blank">
                        点击查看教程
                    </a>
                </div>
            ),
        });

        return;
    }

    const ewaybillAccount = globalStore.ewaybillAccountStore.ewaybillAccountMapping.get(
        template.ewaybillShippingAddressId,
    );

    const encryptedErrorMessage = checkEncryptedManualOrderTemplateRule(orders, template, ewaybillAccount);

    if (encryptedErrorMessage) {
        Modal.error({ title: "打印失败", content: encryptedErrorMessage });

        return;
    }

    const jdConsolidationMessage = checkJdConsolidationOrderTemplateRule(orders, template);

    if (jdConsolidationMessage) {
        Modal.error({ title: "打印失败", content: jdConsolidationMessage });

        return;
    }

    return true;
}

export function checkDouDianPeriodPurchase(orders: OrderDTO[]) {
    for (const order of orders || []) {
        if (
            order.doudianOrderExtraInfo?.periodPurchaseInfoList?.length &&
            order.tags?.includes(OrderTag.PERIOD_PURCHASE)
        ) {
            const selectedOrderItemLength = order.orderItems.filter(({ isSelected }) => isSelected).length;

            if (selectedOrderItemLength >= 2) {
                return "你选择的订单内有周期购订单，每次只能发货当期商品，无法选择多期商品发货。请重新选择周期购订单商品打印，以免打印多期商品错误发货造成损失";
            }
        }
    }

    return null;
}

export function checkTbShipBySfJc(orders: OrderDTO[], template: WaybillTemplateDTO) {
    if ([LogisticsCompany.SF].includes(template?.logisticsCompany)) return null;

    for (const order of orders || []) {
        if (order.tags?.includes(OrderTag.TAOTIAN_SHIP_BY_SF_JC)) {
            return "你选择的订单内有加价发顺丰集采订单，请选择顺丰模板";
        }
    }

    return null;
}

export function checkAliWarehouse(orders: OrderDTO[]) {
    if (isAnyOrderWithAliWarehouse(orders)) {
        return "1688推出产地云仓代发服务能力，商家选择加入后，云仓代发订单由云仓代商家发货取号履约，无需商家使用打单软件取号";
    }
}

export function checkPddWarehouse(orders: OrderDTO[]) {
    if (isAnyOrderWithPddWarehouse(orders)) {
        return "共享仓订单（含承诺送达共享仓订单）由平台直推共享仓出库发货";
    }
}

export function checkJdPetItem(orders: OrderDTO[]) {
    if (isAnyOrderWithJDPetItem(orders)) {
        return "安心宠订单请到京麦商家后台进行处理";
    }
}

/**
 * 抖音电子面单打印手工订单时(非平台售后加密订单）
 * 1 判断订单地址的买家姓名或者地址最后是否有分机号，没有则正常打印
 * 2 如果有分机号，需要判断选择的抖音模板是否有”多分机号派送说明“打印项，有则正常打印
 * 3 如果模板没有该打印项，则提示需要去添加，不阻止打印，商家可以继续打印
 *
 */

export async function checkManualOrderPrintDyEwaybill(
    globalStore: typeof GlobalStore,
    orders: OrderDTO[],
    waybillTemplate: WaybillTemplateDTO,
    shopType?: ShopType,
) {
    const showTipShopTypes = [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL];

    if (!showTipShopTypes.includes(shopType)) return;

    /** 仅提示抖店模板 */
    if (waybillTemplate.type !== WaybillTemplateType.JINRITEMAI) return;

    /** 有虚拟号提示字段，不提示 */
    const hasVirtualIdentifyNoField = waybillTemplate.fields?.some(
        (field) => field.key === WaybillTemplateField.MULT_VIRTUAL_NO_TIP,
    );
    if (hasVirtualIdentifyNoField) return;

    let showTip = false;

    for (let i = 0; i < orders.length; i++) {
        const order = orders[i];

        if (order.tags?.includes(OrderTag.ENCRYPTED_MANUAL_ORDER_DOUDIAN)) continue;

        if (matchVirtualIdentifyNo(order.consignee?.name) || matchVirtualIdentifyNo(order.consignee?.street)) {
            showTip = true;
            break;
        }

        if (!shopType) {
            const orderShopType = globalStore.shopMapping.get(order.shopId)?.type;

            if (showTipShopTypes.includes(orderShopType)) {
                showTip = true;
                break;
            }
        }
    }

    if (!showTip) return;

    await new Promise((resolve, rejected) => {
        const { destroy } = Modal.confirm({
            content: (
                <div>
                    抖音电子面单打印虚拟号会进行二次转换，联系到买家需要输入2个不同的分机码。为防止快递员不知道如何联系买家，请添加"多分机号派送说明"到模板
                    <a
                        className="ml5"
                        href="https://qw15p3s8hv.feishu.cn/wiki/OPecwpV6ZizraekpRleci7M1nUh?from=from_copylink"
                        target="_blank"
                    >
                        查看多分机号派送说明教程
                    </a>
                </div>
            ),
            okCancel: true,
            okText: "去设置模板",
            okButtonProps: {
                onClick() {
                    rejected(new Error("用户取消操作"));

                    destroy();

                    globalStore.history.push("/setting/waybillTemplate");
                },
            },
            cancelText: "继续打印",
            cancelButtonProps: {
                onClick() {
                    resolve(null);

                    destroy();
                },
            },
            closable: true,
            onCancel() {
                rejected(new Error("用户取消操作"));
            },
        });
    });
}

/**
 *
 * @returns  是否已经提示过
 */
export async function checkWxTemplateEwaybillAccountUsername(
    globalStore: typeof GlobalStore,
    shop: ShopDTO,
    template: WaybillTemplateDTO,
) {
    /** 设置不再提示 */
    if (localStorage.getItem(CHANNEL_SHOP_EWAYBILL_TIP_CACHE_2025_11_24)) return true;

    if (
        ![ShopType.CHANNEL_SHOP, ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(
            shop?.type,
        )
    )
        return false;

    await globalStore.ewaybillAccountStore.ensure();

    const templateEwaybillAccount = globalStore.ewaybillAccountStore.ewaybillAccountMapping.get(
        template.ewaybillShippingAddressId,
    );

    if (!templateEwaybillAccount) return false;

    if (
        [
            EwaybillType.CHANNEL_SHOP,
            EwaybillType.CHANNEL_SHOP_SHARE,
            EwaybillType.CHANNEL_SHOP_AUTH,
            EwaybillType.CHANNEL_SHOP_DROPSHIP,
        ].includes(templateEwaybillAccount.type) &&
        templateEwaybillAccount.platformUsername !== shop.sellerUsername
    ) {
        return await new Promise((yes) => {
            globalStore.modalStore.openModal(WxEwaybillTipModal, {
                onOk() {
                    yes(true);
                },
            });
        });
    }

    return false;
}

export function tbShipBySfJcConfirm() {
    return new Promise<boolean>((resolve, reject) => {
        Modal.confirm({
            onOk() {
                resolve(true);
            },
            onCancel() {
                reject(new Error("用户取消操作"));
            },
            title: "是否继续打印",
            okText: "继续打印",
            width: 585,
            focusable: { focusTriggerAfterClose: true },
            content: (
                <div>
                    <div>该订单需使用顺丰速运履约，请先添加顺丰模板再来打印。</div>
                    <div>
                        特别提示：如顺丰顺丰速运无月结卡号，添加模板时请勾选：顺丰集采{" "}
                        <a
                            href={`/setting/waybillTemplate/repository?logisticsCompany=${LogisticsCompany.SF}`}
                            target="_self"
                        >
                            去添加
                        </a>
                    </div>
                </div>
            ),
        });
    });
}
